import React, { ReactElement } from 'react';
import { Box, Dialog, Typography, LinearProgress } from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { gray80, orange50 } from '../../../constants/colors';
import { HELP_FAQ_LAYOUT_NOTATION } from '../../../constants/externalUrls';
import { ReactComponent as LoadingIcon } from '../../assets/loading_icon.svg';
import { ReactComponent as LoadingImg1 } from '../../assets/loading_img1.svg';
import { ReactComponent as LoadingImg2 } from '../../assets/loading_img2.svg';
import { useProxyDialogSx } from '../../../hooks/useProxyDialogSx';

type Props = {
  open: boolean;
  progress: number; // 0-100
  message?: string;
};

const styles = {
  dialog: {
    '& .MuiPaper-root': {
      borderRadius: '16px',
      boxSizing: 'border-box',
      margin: '16px',
      padding: '24px 64px',
      width: '1200px',
      maxWidth: '90vw',
      maxHeight: 'calc(100vh - 32px)',
      '@media screen and (max-width: 960px)': {
        padding: '20px 24px',
      },
    },
  },
  rotatingIcon: {
    width: '64px',
    height: '64px',
    animation: 'spinWithPause 2s ease-in-out infinite',
    '@keyframes spinWithPause': {
      '0%': {
        transform: 'rotate(0deg)',
      },
      '50%': {
        transform: 'rotate(360deg)',
      },
      '100%': {
        transform: 'rotate(360deg)',
      },
    },
  },
  content: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '16px',
  },
  title: {
    color: '#0A0A0A',
    textAlign: 'center',
    fontFamily: '"Noto Sans JP"',
    fontSize: '24px',
    fontStyle: 'normal',
    fontWeight: 700,
    lineHeight: '140%',
    letterSpacing: '2.4px',
    textTransform: 'uppercase',
    '@media screen and (max-width: 960px)': {
      fontSize: '20px',
      letterSpacing: '2px',
    },
  },
  illustrationContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '200px', // 画像切り替え時の高さ変動を防止
    '@media screen and (max-width: 960px)': {
      minHeight: '160px',
    },
  },
  illustrationWrapper: {
    backgroundColor: '#F6F7F8',
    padding: '20px 32px',
  },
  description: {
    fontSize: '14px',
    color: gray80,
    textAlign: 'center',
    lineHeight: 1.8,
  },
  link: {
    fontSize: '14px',
    color: orange50,
    textDecoration: 'none',
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    cursor: 'pointer',
    '&:hover': {
      textDecoration: 'underline',
    },
  },
  progressContainer: {
    width: '100%',
    marginTop: '8px',
  },
  progressBar: {
    height: '8px',
    borderRadius: '4px',
    backgroundColor: '#FEE2D5',
    '& .MuiLinearProgress-bar': {
      backgroundColor: orange50,
      borderRadius: '4px',
    },
  },
  progressInfo: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginTop: '8px',
  },
  progressMessage: {
    fontSize: '14px',
    color: gray80,
  },
  progressPercent: {
    fontSize: '14px',
    color: gray80,
    fontWeight: 500,
  },
};

const LoadingModal = ({
  open,
  progress,
  message = '読取パターンを作成しています...',
}: Props): ReactElement => {
  const proxyDialogSx = useProxyDialogSx();
  // 50%を境にコンテンツを切り替え
  const isSecondPhase = progress >= 50;

  return (
    <Dialog open={open} sx={[styles.dialog, proxyDialogSx]}>
      <Box sx={styles.content}>
        {/* ロゴアイコン（回転） */}
        <Box sx={styles.rotatingIcon}>
          <LoadingIcon style={{ width: '100%', height: '100%' }} />
        </Box>

        <Typography sx={styles.title}>
          {isSecondPhase
            ? 'バラバラな帳票を、いつも同じ形のデータに。'
            : '表記ゆれやレイアウトの違いは、AIが吸収。'}
        </Typography>

        {/* イラスト画像 */}
        <Box sx={styles.illustrationContainer}>
          <Box sx={styles.illustrationWrapper}>
            {isSecondPhase ? (
              <LoadingImg2 style={{ width: '480px', maxWidth: '100%', height: 'auto' }} />
            ) : (
              <LoadingImg1 style={{ width: '480px', maxWidth: '100%', height: 'auto' }} />
            )}
          </Box>
        </Box>

        <Typography sx={styles.description}>
          {isSecondPhase ? (
            <>
              書式が異なっていても、読取パターンに基づいて自動的に整理し、
              <br />
              使いやすいデータを生成します。
            </>
          ) : (
            <>
              「商品」「品名」といった言葉の違いや、取引先ごとに異なるレイアウトも、
              <br />
              AIが文脈を理解して柔軟に読み取ります。
            </>
          )}
        </Typography>

        <Box
          sx={styles.link}
          onClick={(): void => {
            window.open(HELP_FAQ_LAYOUT_NOTATION, '_blank');
          }}
        >
          <Typography sx={{ fontSize: '14px', color: orange50 }}>詳しく見る</Typography>
          <OpenInNewIcon sx={{ fontSize: '14px', color: orange50 }} />
        </Box>

        {/* プログレスバー */}
        <Box sx={styles.progressContainer}>
          <LinearProgress variant="determinate" value={progress} sx={styles.progressBar} />
          <Box sx={styles.progressInfo}>
            <Typography sx={styles.progressMessage}>{message}</Typography>
            <Typography sx={styles.progressPercent}>{Math.round(progress)}%</Typography>
          </Box>
        </Box>
      </Box>
    </Dialog>
  );
};

export default LoadingModal;
